<script setup lang="ts">
const documentId = inject("documentId") as any;
const thesaurusStore = useDocumentThesaurusStore(documentId);
const { currentPage } = storeToRefs(thesaurusStore);

// Group words by first letter
const groupedWords = computed(() => {
  if (!currentPage.value.data?.words) return {};

  const groups: Record<string, string[]> = {};

  currentPage.value.data.words.forEach((word: string) => {
    const firstLetter = word[0].toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(word);
  });

  return groups;
});

// Get sorted letters
const availableLetters = computed(() => {
  return Object.keys(groupedWords.value).sort();
});

// Scroll to letter section
const scrollToLetter = (letter: string) => {
  const element = document.getElementById(`letter-${letter}`);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};
</script>

<template>
  <div
    v-if="currentPage.type == ThesaurusPageType.wordList"
    class="relative h-full flex flex-col min-h-0"
  >
    <!-- Word List -->
    <div class="flex flex-col gap-4 pr-10 h-full min-h-0 overflow-auto">
      <div
        v-for="letter in availableLetters"
        :key="letter"
        :id="`letter-${letter}`"
      >
        <h3 class="font-bold text-body-1 mb-2 text-secondary-default">
          {{ letter }}
        </h3>
        <div class="flex flex-row flex-wrap gap-1">
          <DocumentEditorReviewPanelThesaurusTappableWord
            v-for="word in groupedWords[letter]"
            :key="word"
            :word="word"
            :disabled="currentPage.data?.isReadOnly"
            @click="
              thesaurusStore.addPage({
                type: ThesaurusPageType.word,
                key: word,
                data: undefined,
                isLoading: ref(true),
                label: word,
              })
            "
          >
            {{ word }}
          </DocumentEditorReviewPanelThesaurusTappableWord>
        </div>
      </div>
    </div>

    <!-- Alphabetical Index - positioned relative to parent container -->
    <div
      class="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-[2px] text-xs"
    >
      <button
        v-for="letter in availableLetters"
        :key="letter"
        @click="scrollToLetter(letter)"
        class="w-6 h-6 flex items-center justify-center hover:bg-light-default rounded cursor-pointer text-secondary-default font-medium transition-colors"
      >
        {{ letter }}
      </button>
    </div>
  </div>
</template>

<style scoped></style>
